import React, { useMemo } from 'react'

import { format } from 'date-fns'
import { useRouter } from 'next/router'

import { replaceAlertMessageValues } from '../../features/AlertFlag/useAlertFlag'
import { adjustLabelForFicoAlertMessage, alertMessage, alertType, ficoAlerts } from './alert'
import { useAlertResolve } from './useAlertResolve'
import useAtypicalityAlerts from './useAtypicalityAlerts'
import { nonDismissibleAlertMessages } from '@/constants/alert'
import { useGetApplicationQuery } from '@/graphql/services/application'
import { useUpdateAlertMutation } from '@/services/alerts'
import { useGetAtypicalitCheckCalcsQuery } from '@/services/v2-application/atypicalityCheck'
import { Alerts, User } from '@/types/unlock'

export default function AlertBody(props: Alerts & { user: User; reset: () => void }) {
  const router = useRouter()
  const { id } = router.query
  const { openAlertDestination } = useAlertResolve(id)

  const {
    created_at,
    status,
    message,
    values,
    type,
    uuid,
    user,
    reset,
    application: application_id,
  } = props
  const [updateAlert] = useUpdateAlertMutation()

  const { data: application } = useGetApplicationQuery(
    { externalId: id as string },
    {
      skip: !id,
    }
  )

  const { data: atypicalityCheckCalcs } = useGetAtypicalitCheckCalcsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const atypicalityAlertMessages = useAtypicalityAlerts(atypicalityCheckCalcs)

  const alertMessages = useMemo(() => {
    return { ...alertMessage, ...atypicalityAlertMessages }
  }, [atypicalityAlertMessages])

  const handleStatusChange = (status) => {
    try {
      updateAlert({
        uuid,
        status,
        application_id,
      })
      reset()
    } catch (error) {
      console.error(error)
    }
  }

  const getAlertMessage = (message: string, values: Record<string, string | number>): string => {
    let alertMessage: string = alertMessages?.[message]?.message || ''
    alertMessage = replaceAlertMessageValues(alertMessage, values)

    if (ficoAlerts.includes(message)) {
      alertMessage = adjustLabelForFicoAlertMessage(alertMessage, application?.pricingVersion)
    }

    return alertMessage
  }

  return (
    <div className={`card bg-base-100 shadow-md border`}>
      <div className='flex flex-col gap-0'>
        <div
          className={`flex justify-between px-3 py-2 rounded-t-box  ${
            status == 'no-status' && `bg-error/85`
          }`}
        >
          <div className='flex flex-row gap-2'>
            <p className='text-sm font-bold card-title'>
              {alertType?.[type as string] ?? 'System'}
            </p>
          </div>
          <div className='flex flex-row'>
            <p className='flex items-center text-[10px] font-light'>
              {created_at && format(new Date(created_at), 'MM/dd/yyyy h:mm a')}
            </p>
          </div>
        </div>
        <div className='flex flex-col w-full px-3 py-2'>
          <div className='flex flex-col text-sm break-all gap-3'>
            <p className='break-normal'>Alert Name: {getAlertMessage(message, values)}</p>
            <div className='flex justify-end gap-2 text-xs'>
              {status == 'no-status' && (
                <>
                  <p
                    className='underline cursor-pointer'
                    onClick={() => openAlertDestination(message)}
                  >
                    Resolve
                  </p>
                  {(!nonDismissibleAlertMessages.includes(message) || user?.is_superuser) && (
                    <>
                      <div>|</div>
                      <p
                        className='underline cursor-pointer'
                        onClick={() => handleStatusChange('dismissed')}
                      >
                        Dismiss
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
